import { APP_VERSION, VERSION_STORAGE_KEY, LAST_VERSION_KEY, AppVersion } from '@/config/version';
import { errorReporter } from '@/services/errorReportingService';

// Cache keys to remove when clearing storage
const CACHE_KEYS_TO_REMOVE = ['cachedPricingData', 'lastSync', 'tempData', 'oldVersionData'];

// Where to send the visitor after login / onboarding. These are an in-flight
// navigation, not stale app data, so a version-change wipe must keep them —
// wiping them dropped the note a returning visitor was opening.
const SESSION_KEYS_TO_KEEP = ['redirectAfterLogin', 'postOnboardingRedirect'];

// Timeout for IndexedDB cleanup operations
const INDEXEDDB_DELETE_TIMEOUT_MS = 2000;

// Helper to add timeout to promises - defined at module level to avoid generic parsing issues
function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([promise, new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms))]);
}

export class VersionService {
  private static instance: VersionService;

  private constructor() {}

  public static getInstance(): VersionService {
    if (!VersionService.instance) {
      VersionService.instance = new VersionService();
    }
    return VersionService.instance;
  }

  /**
   * Get current app version
   */
  public getCurrentVersion(): AppVersion {
    return APP_VERSION;
  }

  /**
   * Get stored version from localStorage
   */
  private getStoredVersion(): AppVersion | null {
    try {
      const stored = localStorage.getItem(VERSION_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      errorReporter.expected('Failed to read stored version', error, {
        feature: 'app',
        operation: 'readStoredVersion',
      });
      return null;
    }
  }

  /**
   * Store current version in localStorage
   * Returns true if successful, false if storage failed
   */
  private storeCurrentVersion(): boolean {
    try {
      localStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify(APP_VERSION));
      localStorage.setItem(LAST_VERSION_KEY, APP_VERSION.version);
      return true;
    } catch (error) {
      // Storage might be full or disabled - this is critical for preventing loops
      // Try to clear some space and retry once
      try {
        // Remove old cache keys that might be taking up space
        CACHE_KEYS_TO_REMOVE.forEach((key) => localStorage.removeItem(key));
        // Retry storing version
        localStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify(APP_VERSION));
        localStorage.setItem(LAST_VERSION_KEY, APP_VERSION.version);
        return true;
      } catch {
        return false;
      }
    }
  }

  /**
   * Clear localStorage and sessionStorage, carrying the listed sessionStorage
   * entries across the clear.
   *
   * @param keepSessionKeys sessionStorage keys to keep
   */
  private clearStorage(keepSessionKeys: string[]): void {
    const kept = keepSessionKeys.flatMap((key) => {
      const value = sessionStorage.getItem(key);
      return value === null ? [] : [[key, value] as const];
    });
    localStorage.clear();
    sessionStorage.clear();
    kept.forEach(([key, value]) => sessionStorage.setItem(key, value));
  }

  /**
   * Clear what an older app version left in storage. Never navigates: the
   * running bundle already is the new version, so the visitor stays on the
   * page they opened.
   */
  public checkVersionAndHandleUpdate(): void {
    const storedVersion = this.getStoredVersion();
    const currentVersion = this.getCurrentVersion();

    // First time user or no stored version
    if (!storedVersion) {
      this.storeCurrentVersion();
      return;
    }

    // Only check major.minor.patch, ignore build number
    const storedParts = storedVersion.version.split('.');
    const currentParts = currentVersion.version.split('.');

    // Compare only first 3 parts (major.minor.patch)
    const storedMainVersion = storedParts.slice(0, 3).join('.');
    const currentMainVersion = currentParts.slice(0, 3).join('.');

    // Version has changed (ignoring build number)
    if (storedMainVersion !== currentMainVersion) {
      // Clear everything except a pending post-login destination. This used to
      // also force /login, which bounced every returning visitor's first link
      // after a release (share links included) to the login page and lost it.
      this.clearStorage(SESSION_KEYS_TO_KEEP);

      // Store new version AFTER clearing
      this.storeCurrentVersion();
    }
  }

  /**
   * Clear application cache data (but keep user logged in)
   * IMPORTANT: Excludes Firebase databases to prevent auth corruption
   */
  private async clearApplicationData(): Promise<void> {
    // Firebase IndexedDB names to preserve
    const isFirebaseDb = (name: string): boolean => {
      const lowerName = name.toLowerCase();
      return lowerName.includes('firebase');
    };

    try {
      // Clear specific cache keys instead of everything
      CACHE_KEYS_TO_REMOVE.forEach((key) => {
        localStorage.removeItem(key);
      });

      // Clear only temporary session data
      const sessionKeysToRemove = ['tempSelection', 'draftData'];
      sessionKeysToRemove.forEach((key) => {
        sessionStorage.removeItem(key);
      });

      // Clear IndexedDB (if used) - EXCLUDING Firebase databases
      if ('indexedDB' in window && typeof indexedDB.databases === 'function') {
        try {
          const databases = await indexedDB.databases();
          const nonFirebaseDbs = databases.filter((db) => db.name && !isFirebaseDb(db.name));

          await Promise.allSettled(
            nonFirebaseDbs.map((db) => {
              if (db.name) {
                return new Promise<void>((resolve) => {
                  const deleteRequest = indexedDB.deleteDatabase(db.name!);
                  deleteRequest.onsuccess = () => resolve();
                  deleteRequest.onerror = () => resolve(); // Don't reject, just continue
                  setTimeout(() => resolve(), INDEXEDDB_DELETE_TIMEOUT_MS); // Timeout fallback
                });
              }
              return Promise.resolve();
            }),
          );
        } catch {
          // Silently continue - cache clearing is best-effort
        }
      }

      // Clear service worker caches
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.allSettled(cacheNames.map((cacheName) => caches.delete(cacheName)));
        } catch {
          // Silently continue
        }
      }
    } catch {
      // Silently continue - don't let cache clearing errors break the app
    }
  }

  /**
   * Force a hard refresh of the page with aggressive cache busting
   */
  public async forceRefresh(): Promise<void> {
    // Clear all caches before refresh - MUST await to prevent corruption
    await this.clearBrowserCaches();

    // Add cache busting parameters
    const url = new URL(window.location.href);
    url.searchParams.set('v', APP_VERSION.version);
    url.searchParams.set('t', Date.now().toString());
    url.searchParams.set('_bust', Math.random().toString(36).substring(7));

    // Force reload from server (not cache)
    window.location.replace(url.toString());
  }

  /**
   * Clear browser caches more aggressively
   * IMPORTANT: Excludes Firebase databases to prevent auth corruption
   */
  private async clearBrowserCaches(): Promise<void> {
    // Firebase IndexedDB names to preserve (auth persistence)
    const FIREBASE_DB_PREFIXES = ['firebase', 'firebaseLocalStorage'];

    const isFirebaseDb = (name: string): boolean => {
      const lowerName = name.toLowerCase();
      return FIREBASE_DB_PREFIXES.some(
        (prefix) => lowerName.startsWith(prefix.toLowerCase()) || lowerName.includes('firebase'),
      );
    };

    try {
      // Clear service worker caches (with timeout)
      if ('caches' in window) {
        const cacheNames = await withTimeout(caches.keys(), 3000, []);
        await Promise.allSettled(cacheNames.map((cacheName) => caches.delete(cacheName)));
      }

      // Unregister service workers (with timeout)
      if ('serviceWorker' in navigator) {
        const registrations = await withTimeout(navigator.serviceWorker.getRegistrations(), 3000, []);
        await Promise.allSettled(registrations.map((registration) => registration.unregister()));
      }

      // Clear IndexedDB - EXCLUDING Firebase databases
      if ('indexedDB' in window && typeof indexedDB.databases === 'function') {
        const databases = await withTimeout(indexedDB.databases(), 3000, []);
        const nonFirebaseDbs = databases.filter((db) => db.name && !isFirebaseDb(db.name));

        await Promise.allSettled(
          nonFirebaseDbs.map((db) => {
            if (db.name) {
              return new Promise<void>((resolve) => {
                const deleteRequest = indexedDB.deleteDatabase(db.name!);
                deleteRequest.onsuccess = () => resolve();
                deleteRequest.onerror = () => resolve(); // Don't reject, just continue
                // Timeout for individual DB deletion
                setTimeout(() => resolve(), INDEXEDDB_DELETE_TIMEOUT_MS);
              });
            }
            return Promise.resolve();
          }),
        );
      }
    } catch (error) {
      // Silently continue - cache clearing is best-effort
      // The page will reload regardless, and a fresh load will work
    }
  }

  /**
   * Get version info for display or API consumption
   */
  public getVersionInfo(): AppVersion & { isLatest: boolean } {
    const storedVersion = this.getStoredVersion();

    // Use same comparison logic as checkVersionAndHandleUpdate()
    // Only compare major.minor.patch, ignore build number
    if (!storedVersion) {
      return {
        ...APP_VERSION,
        isLatest: true,
      };
    }

    const storedParts = storedVersion.version.split('.');
    const currentParts = APP_VERSION.version.split('.');

    // Compare only first 3 parts (major.minor.patch)
    const storedMainVersion = storedParts.slice(0, 3).join('.');
    const currentMainVersion = currentParts.slice(0, 3).join('.');

    return {
      ...APP_VERSION,
      isLatest: storedMainVersion === currentMainVersion,
    };
  }

  /**
   * Check remote version to detect if we're serving stale content
   */
  public async checkRemoteVersion(): Promise<{ isStale: boolean; remoteVersion?: AppVersion }> {
    // Timeout for version check - don't let network issues block the app
    const VERSION_CHECK_TIMEOUT_MS = 5000;

    try {
      // Add cache busting to ensure fresh data
      const uniqueId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
      const cacheBuster = `?_t=${Date.now()}&_r=${uniqueId}`;

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), VERSION_CHECK_TIMEOUT_MS);

      const response = await fetch(`/api/version.json${cacheBuster}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to fetch remote version: ${response.status}`);
      }

      const remoteVersion: AppVersion = await response.json();
      const currentVersion = this.getCurrentVersion();

      // Compare build times to detect stale content
      const currentBuildTime = new Date(currentVersion.buildTime).getTime();
      const remoteBuildTime = new Date(remoteVersion.buildTime).getTime();

      const isStale = remoteBuildTime > currentBuildTime || remoteVersion.version !== currentVersion.version;

      return {
        isStale,
        remoteVersion,
      };
    } catch (error) {
      errorReporter.expected('Failed to check remote version', error, {
        feature: 'app',
        operation: 'checkRemoteVersion',
      });
      return { isStale: false };
    }
  }

  /**
   * Enhanced version check that includes remote version comparison
   */
  public async enhancedVersionCheck(): Promise<boolean> {
    // Prevent infinite refresh loops - if we already refreshed this session, skip
    const REFRESH_FLAG = 'version_refresh_completed';
    if (sessionStorage.getItem(REFRESH_FLAG)) {
      return false;
    }

    try {
      // First clear what an older version left in storage (never navigates)
      this.checkVersionAndHandleUpdate();

      // Skip remote version check in development mode
      if (import.meta.env.DEV) {
        return false;
      }

      // Then check if remote version is newer (production only)
      const { isStale, remoteVersion } = await this.checkRemoteVersion();

      if (isStale && remoteVersion) {
        // Set flag BEFORE clearing to prevent loops
        sessionStorage.setItem(REFRESH_FLAG, 'true');

        // Clear everything except our refresh flag and a pending post-login destination
        this.clearStorage([REFRESH_FLAG, ...SESSION_KEYS_TO_KEEP]);

        // Store version to prevent repeated checks
        const stored = this.storeCurrentVersion();
        if (!stored) {
          // If we can't store the version, don't refresh - it would create a loop
          // Just continue with the app as-is
          return false;
        }
        await this.forceRefresh();
        return true;
      }

      return false;
    } catch (error) {
      errorReporter.expected('Enhanced version check failed', error, {
        feature: 'app',
        operation: 'enhancedVersionCheck',
      });
      return false;
    }
  }
  /**
   * Manual version check (for debugging or admin purposes)
   */
  public async forceVersionCheck(): Promise<'refreshing' | 'cache_cleared' | 'up_to_date'> {
    const versionChanged = await this.enhancedVersionCheck();
    if (versionChanged) {
      if (window.confirm('Application has been updated. The page will refresh to load the latest version.')) {
        await this.forceRefresh();
      }
      return 'refreshing';
    }

    // Check if build metadata (buildTime or gitCommit) has changed
    const storedVersion = this.getStoredVersion();
    const currentVersion = this.getCurrentVersion();

    const buildTimeChanged = storedVersion?.buildTime !== currentVersion.buildTime;
    const gitCommitChanged = storedVersion?.gitCommit !== currentVersion.gitCommit;

    if (buildTimeChanged || gitCommitChanged) {
      await this.clearApplicationData();
      this.storeCurrentVersion();
      return 'cache_cleared';
    }

    this.storeCurrentVersion();
    return 'up_to_date';
  }
}

// Export singleton instance
export const versionService = VersionService.getInstance();
